import * as React from 'react'

import { useQuery } from '@apollo/client/react'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import { MentionCountsQuery } from './useChannelMentionCounts'

/**
 * Only presence matters here, so cap the per-channel count at one and let the
 * backend stop counting there.
 */
const PRESENCE_LIMIT = 1

type UseHasChannelMentionsProps = {
  /** Channels to check. Callers pass only mention-supported channels. */
  channels: { id: string }[]
  skip?: boolean
}

type UseHasChannelMentionsReturn = {
  /**
   * Whether any of the channels has ever had a mention, in any status.
   * `undefined` until the answer is known — the query is still in flight, was
   * skipped, or failed. Callers must not read `undefined` as "no mentions":
   * that would tell the user their channel has never been mentioned on no
   * evidence.
   */
  hasMentions: boolean | undefined
  loading: boolean
}

/**
 * Answers "have these channels ever had a mention", regardless of status.
 * Buffer does not import mentions from before a channel was connected, so an
 * empty inbox means something different for a channel that has never had one —
 * this is what separates the two mentions empty states.
 */
export function useHasChannelMentions({
  channels,
  skip = false,
}: UseHasChannelMentionsProps): UseHasChannelMentionsReturn {
  const organizationId = useOrganizationId()

  const channelIds = React.useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  // Sorted+joined key keeps the Apollo variables (and cache entry) stable when
  // the channels array identity changes but its contents don't.
  const channelIdsKey = [...channelIds].toSorted().join(',')

  const variables = React.useMemo(
    () => ({
      input: {
        organizationId: organizationId ?? '',
        engagementType: 'mention' as const,
        channelIds,
        limit: PRESENCE_LIMIT,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organizationId, channelIdsKey],
  )

  // Skipping puts the query on Apollo's `standby` policy, which stops it
  // fetching but leaves the last result in place. Derived values have to be
  // gated on the same condition or they outlive the channels they came from.
  const shouldSkip = skip || !organizationId || channelIds.length === 0

  const { data, loading, error } = useQuery(MentionCountsQuery, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip: shouldSkip,
  })

  const hasMentions = React.useMemo(() => {
    if (shouldSkip || error || !data) return undefined
    return data.engagementCountsByChannelIds.counts.some(
      (count) => count.count > 0,
    )
  }, [data, error, shouldSkip])

  return { hasMentions, loading }
}
